import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  Snackbar,
  Grid,
} from "@mui/material";
import {
  getWashingClassSettings,
  updateWashingClassSettings,
  ICarWashClassCombo,
  VehicleType,
  ServiceType,
} from "../../../../services/settings.service";

const VEHICLE_TYPES: VehicleType[] = ["sedan", "cuv", "suv", "minivan", "van"];
const SERVICE_TYPES: ServiceType[] = ["inner", "outer", "both"];

const VEHICLE_TYPE_I18N_KEY: Record<VehicleType, string> = {
  sedan: "settings.washingClass.sedan",
  cuv: "settings.washingClass.cuv",
  suv: "settings.washingClass.suv",
  minivan: "settings.washingClass.minivan",
  van: "settings.washingClass.van",
};

const SERVICE_TYPE_I18N_KEY: Record<ServiceType, string> = {
  inner: "settings.washingClass.innerWash",
  outer: "settings.washingClass.outerWash",
  both: "settings.washingClass.fullWash",
};

type SnackbarState = { open: boolean; message: string; severity: "success" | "error" };

// standard_min/premium_min are server-derived (see backend
// PlatformSettings.applyTierDerivation) — only econom_min, econom_max, and
// standard_max are independently admin-settable. deriveTierFields keeps the
// other two in sync client-side for instant feedback (before any save
// round-trip), using the exact same rule as the backend so the two can never
// disagree: standard_min = econom_max + 1, premium_min = standard_max + 1.
function deriveTierFields(combo: ICarWashClassCombo): ICarWashClassCombo {
  return {
    ...combo,
    standard_min: combo.econom_max + 1,
    premium_min: combo.standard_max + 1,
  };
}

function buildDefault15(): ICarWashClassCombo[] {
  const out: ICarWashClassCombo[] = [];
  for (const vt of VEHICLE_TYPES) {
    for (const st of SERVICE_TYPES) {
      out.push(
        deriveTierFields({
          vehicleType: vt,
          serviceType: st,
          econom_min: 0,
          econom_max: 10,
          standard_min: 0, // overwritten by deriveTierFields below
          standard_max: 26,
          premium_min: 0, // overwritten by deriveTierFields below
        })
      );
    }
  }
  return out;
}

// Only the 3 independent fields are checked here — standard_min/premium_min
// are always internally consistent once derived, so the old overlap checks
// against them are unreachable/meaningless and have been dropped. The one
// remaining independent invariant, mirroring the backend, is that the
// standard tier has positive width.
function findInvalidCombo(
  combos: ICarWashClassCombo[],
  t: TFunction
): string | null {
  const comboLabel = (c: ICarWashClassCombo) =>
    `${t(VEHICLE_TYPE_I18N_KEY[c.vehicleType])} / ${t(
      SERVICE_TYPE_I18N_KEY[c.serviceType]
    )}`;
  const numericFields: (keyof ICarWashClassCombo)[] = ["econom_min", "econom_max", "standard_max"];
  for (const c of combos) {
    for (const f of numericFields) {
      const v = c[f] as number;
      if (typeof v !== "number" || !Number.isInteger(v) || v < 0) {
        return t("settings.washingClass.errNonNegativeInteger", {
          combo: comboLabel(c),
          field: String(f),
        });
      }
    }
    if (c.econom_min > c.econom_max) {
      return t("settings.washingClass.errEconomRange", {
        combo: comboLabel(c),
      });
    }
    if (c.standard_max < c.econom_max + 1) {
      return t("settings.washingClass.errStandardWidth", {
        combo: comboLabel(c),
      });
    }
  }
  if (combos.length !== 15) {
    return t("settings.washingClass.errComboCount", {
      count: combos.length,
    });
  }
  return null;
}

export default function WashingClassSettings() {
  const { t } = useTranslation();
  const [combos, setCombos] = useState<ICarWashClassCombo[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    let mounted = true;
    getWashingClassSettings()
      .then((s) => {
        if (!mounted) return;
        if (Array.isArray(s.combinations) && s.combinations.length === 15) {
          // Re-derive defensively even though the server already returns
          // gap-free combos — keeps this component's invariant self-
          // contained rather than trusting the response shape.
          setCombos(s.combinations.map(deriveTierFields));
        } else {
          setCombos(buildDefault15());
        }
      })
      .catch((e: Error) => {
        if (mounted) {
          setCombos(buildDefault15());
          setSnackbar({ open: true, message: e.message, severity: "error" });
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const currentVt = VEHICLE_TYPES[tab];
  const tabCombos = useMemo(
    () => combos.filter((c) => c.vehicleType === currentVt),
    [combos, currentVt]
  );

  const invalidReason = useMemo(() => findInvalidCombo(combos, t), [combos, t]);

  const setField = (
    vt: VehicleType,
    st: ServiceType,
    field: "econom_min" | "econom_max" | "standard_max",
    rawValue: string
  ) => {
    const parsed = rawValue === "" ? 0 : parseInt(rawValue, 10);
    const value = Number.isNaN(parsed) ? 0 : parsed;
    setCombos((prev) =>
      prev.map((c) =>
        c.vehicleType === vt && c.serviceType === st
          ? deriveTierFields({ ...c, [field]: value })
          : c
      )
    );
  };

  const handleSave = async () => {
    if (invalidReason !== null) return;
    setSaving(true);
    try {
      const updated = await updateWashingClassSettings({ combinations: combos });
      if (updated && Array.isArray(updated.combinations) && updated.combinations.length === 15) {
        setCombos(updated.combinations.map(deriveTierFields));
      }
      setSnackbar({
        open: true,
        message: t("settings.washingClass.savedSuccess"),
        severity: "success",
      });
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: (e && e.message) || "Save failed",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box p={3}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        {t("settings.washingClass.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("settings.washingClass.description")}
      </Typography>

      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              {VEHICLE_TYPES.map((vt) => (
                <Tab key={vt} label={t(VEHICLE_TYPE_I18N_KEY[vt])} />
              ))}
            </Tabs>
          </Box>

          {SERVICE_TYPES.map((st) => {
            const combo = tabCombos.find((c) => c.serviceType === st);
            if (!combo) return null;
            return (
              <Box key={st} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {t(SERVICE_TYPE_I18N_KEY[st])}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label={t("settings.washingClass.economMin")}
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      value={combo.econom_min}
                      onChange={(e) => setField(currentVt, st, "econom_min", e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label={t("settings.washingClass.economMax")}
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      value={combo.econom_max}
                      onChange={(e) => setField(currentVt, st, "econom_max", e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label={t("settings.washingClass.standardMax")}
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      value={combo.standard_max}
                      onChange={(e) => setField(currentVt, st, "standard_max", e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    {/* standard_min / premium_min are server-derived — read-only,
                        computed client-side for instant feedback (deriveTierFields),
                        not just echoed from the last server response. */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("settings.washingClass.standardMinDerived", { value: combo.standard_min })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("settings.washingClass.premiumMinDerived", { value: combo.premium_min })}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          })}

          <Alert severity={invalidReason ? "error" : "info"} sx={{ mb: 2 }}>
            {invalidReason
              ? invalidReason
              : tabCombos
                  .map(
                    (c) =>
                      `${t(SERVICE_TYPE_I18N_KEY[c.serviceType])}: ${c.econom_min}-${c.econom_max} / ${c.standard_min}-${c.standard_max} / ${c.premium_min}+`
                  )
                  .join("  |  ")}
          </Alert>

          <Button
            variant="contained"
            disabled={invalidReason !== null || saving}
            onClick={handleSave}
          >
            {saving ? t("settings.washingClass.saving") : t("settings.washingClass.saveAllChanges")}
          </Button>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
